import { useState, useRef, useEffect, } from "react";
import type { FlagsPanelProps } from "../helpers/types";
import { Flag, CheckCircle, Clock } from "lucide-react";
import {
    getFullLog,
    maskFlag,
    muteFlag,
    dismissFlag
} from "../../../../api/controllerDashboardApis"

import type { FlagRecord, FullLogMeta } from "../../../../api/controllerDashboardApis";

function getFlagTagClass(tp: string, tg: string): string {
    const type = tp?.toLowerCase();
    const tag = tg?.toLowerCase();

    if (type === "pii") return "bg-amber-100 text-amber-800";
    if (tag?.includes("contempt")) return "bg-red-900 text-red-100";

    return "bg-red-100 text-red-800";
}
const getSegmentDuration = (start?: number | string | null, end?: number | string | null): string | null => {
    if (start == null || end == null) return null;

    const toMs = (v: number | string): number => {
        if (typeof v === "string") {
            const parsed = new Date(v).getTime();
            return Number.isFinite(parsed) ? parsed : NaN;
        }
        return v;
    };

    const s = toMs(start);
    const e = toMs(end);

    if (!Number.isFinite(s) || !Number.isFinite(e)) return null;

    const diffMs = Math.max(0, e - s);

    if (diffMs < 60000) {
        const secs = (diffMs / 1000).toFixed(2);
        return `0:${secs.padStart(5, "0")}`;
    }

    const diffSec = Math.floor(diffMs / 1000);
    const mm = Math.floor(diffSec / 60);
    const ss = String(diffSec % 60).padStart(2, "0");

    return `${mm}:${ss}`;
};
const getStatusDotColor = (status: string) => {
    switch (status) {
        case "unresolved":
            return "#F59E0B";
        case "masked":
            return "#8B5CF6";
        case "muted":
            return "#6B7280";
        default:
            return "#10B981";
    }
};

const getStatusBadgeClass = (status: string) => {
    switch (status) {
        case "unresolved":
            return "bg-amber-100 text-amber-700";
        case "masked":
            return "bg-violet-100 text-violet-700";
        case "muted":
            return "bg-gray-100 text-gray-600";
        default:
            return "bg-green-100 text-green-700";
    }
};


const FILTER_LABELS: Record<string, string> = {
    all: "All",
    pii: "PII",
    conduct: "Conduct",
    unresolved: "Unresolved",
} as const;

export function FlagsPanel({
    
    flags,
    setFlags,
    highlightId,
    newFlagId,
    onToast,
    onLoadMore,
    hasMore,
    loadingMore,
}: Readonly<FlagsPanelProps & {
    highlightId?: string;
    newFlagId?: string | null;
}>) {

    const [filt, setFilt] = useState("all");
    const [dismissing, setDismissing] = useState<Set<string>>(new Set());
    const [busyId, setBusyId] = useState<string | null>(null);

    const [showFullLog, setShowFullLog] = useState(false);
    const [logRecords, setLogRecords] = useState<FlagRecord[]>([]);
    const [logMeta, setLogMeta] = useState<FullLogMeta>({ total: 0, page: 1, limit: 5 });
    const [logLoading, setLogLoading] = useState(false);
    const [logPage, setLogPage] = useState(1);
    const [logHasMore, setLogHasMore] = useState(true);


    const listSentinelRef = useRef<HTMLDivElement | null>(null);
    const logSentinelRef = useRef<HTMLDivElement | null>(null);

    const getDismissStyle = (id: string) => {
        const isDismissing = dismissing.has(id);

        return {
            maxHeight: isDismissing ? 0 : 200,
            opacity: isDismissing ? 0 : 1,
            overflow: "hidden" as const,
            transition: "max-height 350ms ease, opacity 350ms ease",
        };
    };

    const getRowHighlightClass = (id: string) => {
        const classes: string[] = [];

        if (highlightId === id) {
            classes.push("ring-2 ring-blue-400 ring-offset-1 rounded animate-flag-highlight");
        }

        if (newFlagId === id) {
            classes.push("new-flag-flash");
        }

        return classes.join(" ");
    };

    const renderFullLogContent = () => {
        if (logLoading && logRecords.length === 0) {
            return (
                <div className="p-6 text-center text-[12px] text-gray-400">
                    Loading…
                </div>
            );
        }

        if (logRecords.length === 0) {
            return (
                <div className="p-6 text-center text-[12px] text-gray-400">
                    No records found.
                </div>
            );
        }

        return logRecords.map((flag) => (
            <div key={flag._id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50">
                <span
                    className="mt-1 w-2 h-2 rounded-full shrink-0"
                    style={{ background: getStatusDotColor(flag.flagStatus) }}
                />

                <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-gray-800">
                        {flag.flagType} · {flag.flagSubType}
                    </div>

                    <div className="text-[11px] text-gray-500 mt-0.5">
                        {flag.cnr}
                        {flag.segmentDuration
                            ? ` · ${flag.segmentDuration}`
                            : (() => {
                                const d = getSegmentDuration(flag.startTime, flag.endTime);
                                return d ? ` · ${d}` : "";
                            })()}
                    </div>

                    <div className="text-[10px] text-gray-400 mt-0.5">
                        ⏱ {flag.timeStamp
                            ? new Date(flag.timeStamp).toLocaleTimeString("en-IN")
                            : "—"}
                    </div>
                </div>

                <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize shrink-0
                    ${getStatusBadgeClass(flag.flagStatus)}`}>
                    {flag.flagStatus}
                </span>
            </div>
        ));
    };

    const fetchFullLog = async (page = 1, append = false) => {
        setLogLoading(true);
        try {
            const result = await getFullLog({ page, limit: 5 });
            setLogRecords(prev => append ? [...prev, ...result.data] : result.data);
            setLogMeta(result.meta);
            setLogPage(page);
            setLogHasMore(page * result.meta.limit < result.meta.total);
        } finally {
            setLogLoading(false);
        }
    };
    useEffect(() => {
        if (!showFullLog) return;
        if (!logHasMore || logLoading) return;

        const el = logSentinelRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    fetchFullLog(logPage + 1, true);
                }
            },
            { threshold: 0.1 },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [showFullLog, logHasMore, logLoading, logPage]);
    useEffect(() => {
        if (!onLoadMore || !hasMore || loadingMore) return;

        const el = listSentinelRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [onLoadMore, hasMore, loadingMore]);


    const handleMask = async (id: string) => {
        setBusyId(id);
        try {
            const updated = await maskFlag(id);
            setFlags(prev =>
                prev.map(f => (f.id === id ? { ...f, flagStatus: updated.flagStatus } : f)),
            );
            onToast("Flag masked — blur job queued", "#5B21B6");
        } catch (err) {
            console.error("[FlagsPanel] maskFlag error:", err);
            onToast("Failed to mask flag", "#EF4444");
        } finally {
            setBusyId(null);
        }
    };

    // FIX: same as handleMask — sync local flagStatus from API response.
    const handleMute = async (id: string) => {
        setBusyId(id);
        try {
            const updated = await muteFlag(id);
            setFlags(prev =>
                prev.map(f => (f.id === id ? { ...f, flagStatus: updated.flagStatus } : f)),
            );
            onToast("Flag muted — audio silence job queued", "#6B7280");
        } catch (err) {
            console.error("[FlagsPanel] muteFlag error:", err);
            onToast("Failed to mute flag", "#EF4444");
        } finally {
            setBusyId(null);
        }
    };

    const performDismiss = async (id: string) => {
        const updated = await dismissFlag(id);

        setFlags(prev =>
            prev.map(f =>
                f.id === id
                    ? { ...f, rv: true, flagStatus: updated.flagStatus }
                    : f,
            ),
        );
    };


    const removeFromDismissing = (id: string) => {
        setDismissing(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const handleDismiss = (id: string) => {
        setDismissing(prev => new Set(prev).add(id));

        const run = async () => {
            await performDismiss(id);
            removeFromDismissing(id);
        };

        setTimeout(run, 350);
    };


    const active = flags.filter(f => !f.rv);

    const visible = active.filter(f => {
        if (filt === "all") return true;
        if (filt === "unresolved") return f.flagStatus === "unresolved";
        return f.tp === filt;
    });

    return (
        <div className="bg-white border border-gray-200 rounded-[10px] overflow-hidden flex flex-col">

            {/* HEADER */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-800">
                    <Flag size={15} className="text-blue-600" />
                    Flagged Content
                </div>

                <button
                    onClick={() => {
                        setShowFullLog(true);
                        fetchFullLog(1);
                    }}
                    className="text-[11px] text-blue-600 hover:underline"
                >
                    View full log ↗
                </button>
            </div>

            {/* FILTERS */}
            <div className="flex gap-1.5 px-4 py-2 border-b border-gray-100">
                {(["all", "pii", "conduct", "unresolved"] as const).map(k => (
                    <button
                        key={k}
                        type="button"
                        onClick={() => setFilt(k)}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-all ${filt === k
                            ? "bg-blue-50 text-blue-700 border-blue-500 font-medium"
                            : "border-gray-200 text-gray-500 hover:bg-gray-50"
                            }`}
                    >
                        {FILTER_LABELS[k]}
                    </button>
                ))}
            </div>
            <div className="flex-1 overflow-auto">
                {visible.length === 0 ? (
                    <div className="flex flex-col items-center py-6 text-[12px] text-gray-400">
                        <CheckCircle size={26} className="text-green-500 mb-1.5" />
                        No active flags — all sessions clean
                    </div>
                ) : (
                    <>
                        {visible.map(f => {
                          
                            const duration =

                                f.dr && f.dr !== "—"
                                    ? f.dr
                                    : getSegmentDuration(f.startTime, f.endTime) ?? "00:00";

                            return (
                                <div
                                    key={f.id}
                                    className={`flex items-start gap-2.5 px-4 py-2.5 border-b border-gray-100 last:border-0 transition-colors duration-700
                                        ${getRowHighlightClass(f.id)}
                                    `}
                                    style={getDismissStyle(f.id)}
                                >
                                    <span className="text-[11px] text-gray-400 min-w-11 pt-px shrink-0">
                                        <Clock size={10} className="inline mr-1" />
                                        {f.tm}
                                    </span>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                                            <span className="text-xs font-medium text-gray-800">{f.rm}</span>
                                            <span className={`text-[9px] px-1.5 py-px rounded ${getFlagTagClass(f.tp, f.tg)}`}>
                                                {f.tg}
                                            </span>
                                        </div>

                                        <div className="text-[11px] text-gray-500">
                                            {f.ds}
                                        </div>

                                        <div className="text-[10px] text-gray-400 mt-0.5">
                                            {duration} seg · Audio + Video
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <button
                                            disabled={busyId === f.id}
                                            onClick={() => handleMask(f.id)}
                                            className="px-2.5 py-1 text-[11px] bg-blue-50 text-blue-700 rounded"
                                        >
                                            Mask
                                        </button>

                                        <button
                                            disabled={busyId === f.id}
                                            onClick={() => handleMute(f.id)}
                                            className="px-2.5 py-1 text-[11px] bg-violet-100 text-violet-700 rounded"
                                        >
                                            Mute
                                        </button>

                                        <button
                                            onClick={() => handleDismiss(f.id)}
                                            className="px-2.5 py-1 text-[11px] bg-gray-100 text-gray-600 rounded"
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        {hasMore && (
                            <div ref={listSentinelRef} className="p-3 text-center text-[11px] text-gray-400">
                                {loadingMore ? "Loading more…" : ""}
                            </div>
                        )}
                    </>
                )}
            </div>


            {showFullLog && (
                <div
                    className="fixed inset-0 z-50 flex justify-end h-screen overflow-hidden"
                    role="dialog"
                    aria-modal="true"
                >
                    {/* Overlay */}
                    <button
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setShowFullLog(false)}
                    />

                    {/* Drawer */}
                    <div className="relative w-96 h-full bg-white shadow-2xl flex flex-col overflow-hidden">

                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
                            <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-800">
                                <Flag size={15} className="text-blue-600" />
                                Full Flag Log
                                <span className="text-[10px] px-1.5 py-px rounded-full bg-amber-100 text-amber-700">
                                    {logMeta?.total ?? 0} total
                                </span>

                            </div>

                            <button
                                onClick={() => setShowFullLog(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-gray-100">
                            {renderFullLogContent()}

                            {logHasMore && (
                                <div ref={logSentinelRef} className="p-3 text-center text-[11px] text-gray-400">
                                    {logLoading ? "Loading more…" : ""}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}